import { insertPoint } from 'prosemirror-transform';
import { TextSelection, NodeSelection } from 'prosemirror-state';
import { CitationWithId, SourceField } from '@sciflow/cite';
import { SFNodeType } from '@sciflow/schema';
import {
  DOMParser as ProseMirrorDOMParser,
  Fragment,
  Node as ProseMirrorNode,
} from 'prosemirror-model';

type CitationTextUpdate =
  | string
  | {
      text?: string;
      html?: string;
    };

/**
 * Sourced from platform/main citation command pattern:
 * packages/editor/core/src/lib/features/citation/command.ts (runUpdateCitationNode)
 * TODO: move this into a shared command library used by both main and SFO.
 */
const runUpdateCitationNode = (
  state,
  position: number,
  options: { attrs: Record<string, unknown>; content?: Fragment; scrollIntoView?: boolean },
  dispatch?,
) => {
  const citationType = state.schema.nodes.citation;
  if (!citationType) {
    return false;
  }

  const nodeAt = state.doc.nodeAt(position);
  if (!nodeAt || nodeAt.type !== citationType) {
    return false;
  }

  const mergedAttrs = { ...nodeAt.attrs, ...options.attrs };
  const newNode = citationType.create(mergedAttrs, options.content ?? nodeAt.content, nodeAt.marks);

  if (dispatch) {
    const tr = state.tr.replaceWith(position, position + nodeAt.nodeSize, newNode);
    dispatch(options.scrollIntoView === false ? tr : tr.scrollIntoView());
  }
  return true;
};

const toCitationFragment = (state: any, textUpdate: CitationTextUpdate): Fragment => {
  if (typeof textUpdate === 'string') {
    return textUpdate ? Fragment.from(state.schema.text(textUpdate)) : Fragment.empty;
  }

  const html = typeof textUpdate?.html === 'string' ? textUpdate.html.trim() : '';
  if (html.length > 0 && typeof document !== 'undefined') {
    try {
      const container = document.createElement('div');
      container.innerHTML = html;
      const parsed = ProseMirrorDOMParser.fromSchema(state.schema).parseSlice(container, {
        preserveWhitespace: true,
      });

      const inlineNodes: ProseMirrorNode[] = [];
      parsed.content.descendants((node) => {
        if (node.isInline) {
          inlineNodes.push(node);
          return false;
        }
        return true;
      });

      if (inlineNodes.length > 0) {
        return Fragment.fromArray(inlineNodes);
      }
    } catch (_error) {
      // Ignore malformed html and fallback to plain text.
    }
  }

  const fallback = typeof textUpdate?.text === 'string' ? textUpdate.text : '';
  return fallback ? Fragment.from(state.schema.text(fallback)) : Fragment.empty;
};

export const updateCitationByIdCommand = (
  citation: CitationWithId,
  textUpdate?: CitationTextUpdate,
) => {
  return (state, dispatch) => {
    state.doc.nodesBetween(0, state.doc.nodeSize - 2, (node, pos) => {
      if (node.attrs?.id !== undefined && node.attrs?.id === citation.citationID) {
        const applied = runUpdateCitationNode(
          state,
          pos,
          {
            attrs: {
              ...node.attrs,
              source: encodeURI(SourceField.toString(citation)),
              id: citation.citationID,
              citationMode: citation.citationMode === 'custom' ? 'custom' : 'generated',
            },
            content: textUpdate === undefined ? undefined : toCitationFragment(state, textUpdate),
          },
          dispatch
            ? (tr) => {
                tr.setSelection(NodeSelection.create(tr.doc, pos));
                dispatch(tr);
              }
            : undefined,
        );
        return !applied;
      }
      return true;
    });

    return true;
  };
};

export const updateCitationByIdSilentCommand = (
  citation: CitationWithId,
  textUpdate?: CitationTextUpdate,
) => {
  return (state, dispatch) => {
    state.doc.nodesBetween(0, state.doc.nodeSize - 2, (node, pos) => {
      if (node.attrs?.id !== undefined && node.attrs?.id === citation.citationID) {
        const applied = runUpdateCitationNode(
          state,
          pos,
          {
            attrs: {
              ...node.attrs,
              source: encodeURI(SourceField.toString(citation)),
              id: citation.citationID,
              citationMode: citation.citationMode === 'custom' ? 'custom' : 'generated',
            },
            content: textUpdate === undefined ? undefined : toCitationFragment(state, textUpdate),
            scrollIntoView: false,
          },
          dispatch,
        );
        return !applied;
      }
      return true;
    });

    return true;
  };
};

export const updateCitationCommand = (text: string, source, pos, nodeSize) => {
  return (state, dispatch) => {
    const tr = state.tr;
    const from = pos;
    const to = pos + nodeSize;

    if (!dispatch) {
      return from != null;
    }

    tr.replaceWith(
      from,
      to,
      state.schema.node(SFNodeType.citation, { source }, [state.schema.text(text)]),
    );
    tr.scrollIntoView();
    dispatch(tr);

    return true;
  };
};

export const setAllCitationsModeCommand = (
  mode: 'generated' | 'custom',
  renderCitation: (citationItems: any[]) => string,
) => {
  return (state, dispatch) => {
    const citationType = state.schema.nodes.citation;
    if (!citationType) {
      return false;
    }

    let tr = state.tr;
    const updates: Array<{ pos: number; node: any }> = [];

    state.doc.nodesBetween(0, state.doc.nodeSize - 2, (node, pos) => {
      if (node.type === citationType) {
        updates.push({ pos, node });
        return false;
      }
      return true;
    });

    // Apply in reverse so positions stay valid.
    for (let i = updates.length - 1; i >= 0; i--) {
      const { pos, node } = updates[i];
      const attrs = { ...node.attrs, citationMode: mode };

      let content = node.content;
      if (mode === 'generated') {
        try {
          const source = node.attrs.source ? JSON.parse(decodeURI(node.attrs.source)) : [];
          const items = Array.isArray(source) ? source : [];
          if (items.length > 0) {
            const text = renderCitation(items);
            if (text) {
              content = Fragment.from(state.schema.text(text));
            }
          }
        } catch (_e) {
          // Keep existing content on parse failure.
        }
      }

      const newNode = citationType.create(attrs, content, node.marks);
      tr = tr.replaceWith(pos, pos + node.nodeSize, newNode);
    }

    if (dispatch && updates.length > 0) {
      dispatch(tr);
    }
    return updates.length > 0;
  };
};

export const insertCitationCommand = (source, renderedCitation = '@') => {
  return (state, dispatch): boolean => {
    const insertAt = insertPoint(state.doc, state.selection.from, state.schema.nodes.citation);
    if (insertAt === null) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      tr.insert(
        state.selection.to,
        state.schema.node(
          SFNodeType.citation,
          {
            source: typeof source !== 'string' ? Math.random().toString(source) : source,
            style: null,
          },
          [state.schema.text(renderedCitation)],
        ),
      );
      tr.setSelection(TextSelection.create(tr.doc, state.selection.to + 1));
      dispatch(tr);
    }
    return true;
  };
};
